import { Colors } from "../../config/DesignConstants";
import { V, V2d } from "../../core/Vector";
import { ApplyFunction } from "./BaseVectorFilter";
import { SegmentFilterContext, VectorSegment } from "./VectorSprite";

/**
 * Apply a color to all segments
 * @param color Either a color value or a function that transforms the existing color
 */
export function applyColor(
  color: number | ((oldColor: number) => number),
): ApplyFunction {
  return (segments: VectorSegment[]) =>
    segments.map((segment) => ({
      ...segment,
      color: typeof color === "function" ? color(segment.color) : color,
    }));
}

/**
 * Apply thickness to all segments
 * @param thickness Either a thickness value or a function that transforms the existing thickness
 */
export function applyThickness(
  thickness: number | ((oldThickness: number) => number),
): ApplyFunction {
  return (segments: VectorSegment[]) =>
    segments.map((segment) => ({
      ...segment,
      thickness:
        typeof thickness === "function"
          ? thickness(segment.thickness || 1)
          : thickness,
    }));
}

/**
 * Apply wobble effect to segment endpoints
 */
export function applyWobble(
  amplitude: number,
  frequency: number,
  seed: number = Math.random() * 1000,
): ApplyFunction {
  return (segments: VectorSegment[], context: SegmentFilterContext) =>
    segments.map((segment, i) => {
      const phaseOffset = (i * 0.7 + seed) * Math.PI;
      const wobbleX =
        Math.sin((context.totalTime + phaseOffset) * frequency * Math.PI * 2) *
        amplitude;
      const wobbleY =
        Math.cos(
          (context.totalTime + phaseOffset * 1.3) * frequency * Math.PI * 2,
        ) * amplitude;

      const wobbleOffset = V(wobbleX, wobbleY);
      return {
        ...segment,
        start: V(
          segment.start.x + wobbleOffset.x,
          segment.start.y + wobbleOffset.y,
        ),
        end: V(segment.end.x + wobbleOffset.x, segment.end.y + wobbleOffset.y),
      };
    });
}

/**
 * Apply offset to all segment endpoints
 */
export function applyOffset(offset: V2d): ApplyFunction {
  return (segments: VectorSegment[]) =>
    segments.map((segment) => ({
      ...segment,
      start: V(segment.start.x + offset.x, segment.start.y + offset.y),
      end: V(segment.end.x + offset.x, segment.end.y + offset.y),
    }));
}

/**
 * Apply scale to all segment endpoints (from origin)
 */
export function applyScale(scale: number): ApplyFunction {
  return (segments: VectorSegment[]) =>
    segments.map((segment) => ({
      ...segment,
      start: V(segment.start.x * scale, segment.start.y * scale),
      end: V(segment.end.x * scale, segment.end.y * scale),
    }));
}

const RAINBOW = [
  Colors.RED,
  Colors.ORANGE,
  Colors.YELLOW,
  Colors.GREEN,
  Colors.BLUE,
  Colors.PURPLE,
];

/**
 * Apply rainbow colors to segments in sequence
 */
export function applyRainbow(
  rainbow: readonly number[] = RAINBOW,
): ApplyFunction {
  return (segments) =>
    segments.map((segment, i) => ({
      ...segment,
      color: rainbow[i % rainbow.length],
    }));
}

/**
 * Apply moving rainbow colors that cycle through segments over time
 */
export function applyMovingRainbow(
  rainbow: readonly number[] = RAINBOW,
  speed: number = 1.0,
): ApplyFunction {
  return (segments, context) => {
    // Calculate time-based offset for the rainbow pattern
    const timeOffset = Math.floor(context.totalTime * speed * rainbow.length);

    return segments.map((segment, i) => ({
      ...segment,
      color: rainbow[(i + timeOffset) % rainbow.length],
    }));
  };
}
